import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { leaderboardAPI, picksAPI } from '../services/api';
import PickBreakdown from '../components/PickBreakdown';

function Leaderboard() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [userPicks, setUserPicks] = useState({});

  useEffect(() => {
    const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await leaderboardAPI.getLeaderboard({ limit });
      
      if (response.success) {
        setLeaderboard(response.data.leaderboard || []);
      }
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
    };
    loadLeaderboard();
  }, [limit]);

  const getRankBadgeClass = (rank) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return 'default';
  };

  const toggleExpanded = async (userId) => {
    const newExpanded = new Set(expandedUsers);
    
    if (newExpanded.has(userId)) {
      newExpanded.delete(userId);
    } else {
      newExpanded.add(userId);
      
      // Fetch picks if not already loaded
      if (!userPicks[userId]) {
        try {
          const response = await picksAPI.getUserPicks(userId, { status: 'completed' });
          if (response.success) {
            setUserPicks(prev => ({
              ...prev,
              [userId]: response.data.picks || []
            }));
          }
        } catch (error) {
          console.error('Failed to load user picks:', error);
        }
      }
    }
    
    setExpandedUsers(newExpanded);
  };

  const renderPicksByRace = (picks) => {
    // Group picks by race
    const picksByRace = picks.reduce((acc, pick) => {
      const raceName = pick.raceName || 'Unknown Race';
      if (!acc[raceName]) {
        acc[raceName] = [];
      }
      acc[raceName].push(pick);
      return acc;
    }, {});

    const totalEarnings = picks.reduce((sum, pick) => sum + (pick.actualPayout || 0), 0);

    return (
      <div style={{ 
        padding: '1.5rem',
        backgroundColor: '#fafafa',
        borderTop: '2px solid #e0e0e0'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #e0e0e0'
        }}>
          <div>
            <h4 style={{ 
              margin: '0 0 0.5rem 0',
              color: '#666',
              fontSize: '0.95rem',
              fontWeight: 'bold'
            }}>
              🏆 Betting History & Earnings Breakdown
            </h4>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#999' }}>
              {Object.keys(picksByRace).length} race(s), {picks.length} winning bet(s)
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              Total Winnings
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#4CAF50'
            }}>
              ${totalEarnings.toFixed(2)}
            </div>
          </div>
        </div>

        {Object.entries(picksByRace).map(([raceName, racePicks]) => {
          const raceEarnings = racePicks.reduce((sum, p) => sum + (p.actualPayout || 0), 0);
          
          return (
            <div key={raceName} style={{
              marginBottom: '1.5rem',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '0.75rem 1rem',
                backgroundColor: '#f5f5f5',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>🏇 {raceName}</span>
                <span style={{
                  fontSize: '0.9rem',
                  color: '#4CAF50',
                  fontWeight: 'bold'
                }}>
                  ${raceEarnings.toFixed(2)}
                </span>
              </div>
              <div style={{ padding: '0.5rem' }}>
                {racePicks.map(pick => (
                  <div key={pick.pickId} style={{ marginBottom: '0.5rem' }}>
                    <PickBreakdown pick={pick} expanded={true} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {picks.length === 0 && (
          <p style={{ color: '#999', fontStyle: 'italic', margin: 0, textAlign: 'center', padding: '2rem' }}>
            No winning bets yet
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Loading leaderboard...</div>;
  }

  return (
    <div className="leaderboard-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>🏆 Leaderboard</h2>
        
        <div>
          <label htmlFor="limit" style={{ marginRight: '0.5rem' }}>Show:</label>
          <select
            id="limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{
              padding: '0.5rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '1rem',
            }}
          >
            <option value="10">Top 10</option>
            <option value="25">Top 25</option>
            <option value="50">Top 50</option>
            <option value="100">Top 100</option>
          </select>
        </div>
      </div>

      {leaderboard.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', color: '#666' }}>
            No leaderboard data available yet. Start making picks!
          </p>
        </div>
      ) : (
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th>Rank</th>
              <th>Player</th>
              <th>Total Winnings</th>
              <th>Picks</th>
              <th>Wins</th>
              <th>Win Rate</th>

            </tr>
          </thead>
          <tbody>
            {leaderboard.map((entry) => {
              const isCurrentUser = entry.userId === user?.userId;
              const isExpanded = expandedUsers.has(entry.userId);
              const picks = userPicks[entry.userId] || [];
              
              return (
                <React.Fragment key={entry.userId}>
                  <tr
                    style={{
                      backgroundColor: isCurrentUser ? '#e3f2fd' : 'transparent',
                      fontWeight: isCurrentUser ? 'bold' : 'normal',
                    }}
                  >
                    <td>
                      {entry.successfulPicks > 0 ? (
                        <button
                          onClick={() => toggleExpanded(entry.userId)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            padding: '0.25rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          title={isExpanded ? 'Collapse' : 'Expand to see winning bets'}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      ) : null}
                    </td>
                    <td>
                      <span className={`rank-badge ${getRankBadgeClass(entry.rank)}`}>
                        {entry.rank}
                      </span>
                    </td>
                  <td>
                    <div>
                      {entry.displayName || entry.username}
                      {isCurrentUser && (
                        <span style={{ marginLeft: '0.5rem', color: '#2196F3', fontSize: '0.9rem' }}>
                          (You)
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                      @{entry.username}
                    </div>
                  </td>
                  <td>
                    <strong style={{ color: '#4CAF50', fontSize: '1.1rem' }}>
                      ${entry.totalWinnings.toFixed(2)}
                    </strong>
                  </td>
                  <td>{entry.totalPicks}</td>
                  <td>{entry.successfulPicks}</td>
                  <td>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      backgroundColor: entry.winRate >= 50 ? '#e8f5e9' : entry.winRate >= 30 ? '#fff3e0' : '#ffebee',
                      color: entry.winRate >= 50 ? '#2e7d32' : entry.winRate >= 30 ? '#e65100' : '#c62828',
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                    }}>
                      {entry.winRate.toFixed(1)}%
                    </span>
                  </td>

                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan="7" style={{ padding: 0 }}>
                      {picks.length > 0 ? (
                        renderPicksByRace(picks)
                      ) : (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
                          Loading winning bets...
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
            })}
          </tbody>
        </table>
      )}

      {!leaderboard.some((entry) => entry.userId === user?.userId) && user?.totalPicks > 0 && (
        <div className="card" style={{ marginTop: '2rem', backgroundColor: '#fff3e0' }}>
          <p style={{ margin: 0, color: '#e65100' }}>
            ℹ️ Your current rank is outside the top {limit}. Keep playing to climb the leaderboard!
          </p>
        </div>
      )}

      <div className="card" style={{ marginTop: '2rem' }}>
        <h3>How Rankings Work</h3>
        <ul style={{ color: '#666', lineHeight: '1.8' }}>
          <li><strong>Total Winnings:</strong> Primary ranking factor - sum of all winning payouts</li>
          <li><strong>Win Rate:</strong> Percentage of successful picks (wins / total picks)</li>
          <li><strong>Base Bet:</strong> All picks use a standard $2.00 bet amount</li>
        </ul>
      </div>
    </div>
  );
}

export default Leaderboard;
